/**
 * TemplateEditor - Create/Edit template page
 *
 * Features:
 * - Create or edit mode based on route
 * - Form with validation
 * - Category selector
 * - Content editor
 * - Visibility controls
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Input,
  Label,
  Textarea,
  Switch,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@respectly/ui';
import {
  ArrowLeft,
  Save,
  FileText,
  Mail,
  MessageSquare,
  ClipboardList,
  Loader2,
} from 'lucide-react';
import { useTemplatesStore } from '../../stores/useTemplatesStore';
import { VisibilityControls } from '../../components/templates';
import type { TemplateCategory, TemplateVisibility } from '@respectly/shared-types';

// ============================================
// Form Schema
// ============================================

const templateFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['email', 'sms', 'document', 'form']),
  category: z.string().optional().nullable(),
  content: z.string().min(1, 'Content is required'),
  isActive: z.boolean(),
});

type TemplateFormData = z.infer<typeof templateFormSchema>;

// ============================================
// Configuration
// ============================================

const categoryOptions = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'document', label: 'Document', icon: FileText },
  { value: 'form', label: 'Form', icon: ClipboardList },
];

// ============================================
// Component
// ============================================

export function TemplateEditor() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const templateId = (params as { id?: string }).id;
  const isEditMode = !!templateId;

  const [visibility, setVisibility] = useState<TemplateVisibility>('all');
  const [visibleToPlans, setVisibleToPlans] = useState<string[]>([]);
  const [visibleToTenants, setVisibleToTenants] = useState<string[]>([]);

  const {
    activeTemplate,
    isLoadingTemplate,
    isPerformingAction,
    loadTemplate,
    createTemplate,
    updateTemplate,
    clearActiveTemplate,
  } = useTemplatesStore();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<TemplateFormData>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: {
      name: '',
      type: 'email',
      category: null,
      content: '',
      isActive: true,
    },
  });

  const selectedType = watch('type');

  // Load template in edit mode
  useEffect(() => {
    if (isEditMode && templateId) {
      loadTemplate(templateId);
    }
    return () => clearActiveTemplate();
  }, [isEditMode, templateId, loadTemplate, clearActiveTemplate]);

  // Populate form when template loads
  useEffect(() => {
    if (activeTemplate && isEditMode) {
      reset({
        name: activeTemplate.name,
        type: activeTemplate.type,
        category: activeTemplate.category,
        content: activeTemplate.content,
        isActive: activeTemplate.isActive,
      });
      setVisibility(activeTemplate.visibility);
      setVisibleToPlans(activeTemplate.visibleToPlans || []);
      setVisibleToTenants(activeTemplate.visibleToTenants || []);
    }
  }, [activeTemplate, isEditMode, reset]);

  const handleVisibilityChange = (
    newVisibility: TemplateVisibility,
    plans?: string[],
    tenants?: string[]
  ) => {
    setVisibility(newVisibility);
    if (plans) setVisibleToPlans(plans);
    if (tenants) setVisibleToTenants(tenants);
  };

  const onSubmit = async (data: TemplateFormData) => {
    const payload = {
      ...data,
      visibility,
      visibleToPlans: visibility === 'plans' ? visibleToPlans : undefined,
      visibleToTenants: visibility === 'tenants' ? visibleToTenants : undefined,
    };

    let success = false;
    if (isEditMode && templateId) {
      success = await updateTemplate(templateId, payload);
    } else {
      const newId = await createTemplate(payload);
      success = !!newId;
    }

    if (success) {
      navigate({ to: '/templates' });
    }
  };

  const handleBack = () => {
    navigate({ to: '/templates' });
  };

  // Loading state
  if (isEditMode && isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                  {isEditMode ? 'Edit Template' : 'Create Template'}
                </h1>
                <p className="text-sm text-slate-500">
                  {isEditMode
                    ? 'Update template details and content'
                    : 'Create a new global template'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleBack}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={isPerformingAction || (!isDirty && isEditMode)}
            >
              {isPerformingAction ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isEditMode ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Template Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="Enter template name"
                  {...register('name')}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={selectedType}
                  onValueChange={(val) => setValue('type', val as TemplateCategory)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <option.icon className="h-4 w-4" />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label>Active</Label>
                  <p className="text-sm text-slate-500">
                    Inactive templates are not visible to tenants
                  </p>
                </div>
                <Switch
                  checked={watch('isActive')}
                  onCheckedChange={(checked) => setValue('isActive', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Content</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="content">
                  {selectedType === 'form' ? 'Form JSON' : 'Template Content'} *
                </Label>
                <Textarea
                  id="content"
                  placeholder={
                    selectedType === 'form'
                      ? 'Enter form JSON definition...'
                      : selectedType === 'sms'
                      ? 'Enter SMS message content (160 char limit recommended)...'
                      : 'Enter template content...'
                  }
                  rows={selectedType === 'sms' ? 4 : 10}
                  {...register('content')}
                  className={cn(
                    'font-mono text-sm',
                    errors.content && 'border-red-500'
                  )}
                />
                {errors.content && (
                  <p className="text-sm text-red-500">{errors.content.message}</p>
                )}
                <p className="text-xs text-slate-500">
                  Use {`{{variable_name}}`} for dynamic content placeholders
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Visibility */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Visibility Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <VisibilityControls
                value={visibility}
                visibleToPlans={visibleToPlans}
                visibleToTenants={visibleToTenants}
                onChange={handleVisibilityChange}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </motion.div>
  );
}
